import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../../../config/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import Navbar from "../../NavBar/Navbar";
import Footer from "../../Footer/Footer";
import "./ServiceDetail.css";

function ServiceDetail() {
  const { slug } = useParams();              // e.g. interior-design
  const [category, setCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [categoryDescription, setCategoryDescription] = useState("");
  const [loading, setLoading] = useState(true);
  // sectionScrolled removed: keep background white at all times
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxScale, setLightboxScale] = useState(1);
  const [shareMessage, setShareMessage] = useState('');

  useEffect(() => {
    const fetchCategoryAndServices = async () => {
      try {
        // 1️⃣  Get category document that matches this slug
        const catQuery = query(collection(db, "categories"), where("slug", "==", slug));
        const catSnap = await getDocs(catQuery);
        if (catSnap.empty) {
          setLoading(false);
          return;
        }

        const catData = catSnap.docs[0].data();
        setCategory(catData.name);
        // prefer category description from CMS if available, otherwise use a richer default
        setCategoryDescription(
          catData.description ||
            'Discover thoughtfully designed projects that combine aesthetics with practical layouts, smart storage, and premium finishes — perfect inspiration for your home.'
        );

        // 2️⃣  Get all services with same category name
        const servQuery = query(collection(db, "services"), where("category", "==", catData.name));
        const servSnap = await getDocs(servQuery);
        const servData = servSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setServices(servData);
        // Fetch other categories to suggest (exclude current slug)
        try {
          const allCatSnap = await getDocs(collection(db, "categories"));
          const allCats = allCatSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const otherCats = allCats.filter((c) => c.slug !== slug).slice(0, 4);
          // If a category doesn't have an image, try to pick an image from one of its services
          const enriched = await Promise.all(
            otherCats.map(async (c) => {
              if (c.image) return c;
              try {
                const servQ = query(collection(db, 'services'), where('category', '==', c.name));
                const servSnap2 = await getDocs(servQ);
                const firstServ = servSnap2.docs[0];
                if (firstServ) {
                  const data = firstServ.data();
                  return { ...c, image: data.image || data.photo || null };
                }
              } catch (e) {
                // ignore
              }
              return c;
            })
          );
          setSuggestions(enriched);
        } catch (e) {
          console.warn('Could not load suggestion categories', e);
        }
      } catch (err) {
        console.error("Error loading services:", err);
      }
      setLoading(false);
    };

    fetchCategoryAndServices();
  }, [slug]);

  // no scroll-based background change — keep section background white

  if (loading) return <p className="loading">Loading...</p>;
  if (!category) return <p className="loading">Category not found</p>;

  return (
    <>
      <Navbar />
  <section className="service-detail-section">
        <div className="service-detail-header container">
          <div className="header-inner">
            <h2>{category}</h2>
            <p className="lead">
              Explore our latest projects and ideas under <b>{category}</b> — curated layouts and finishes you can trust.
            </p>
            {categoryDescription && (
              <p className="category-description">{categoryDescription}</p>
            )}
          </div>
        </div>

        <div className="service-detail-gallery container">
          {services.length === 0 && (
            <p className="no-items">No images found for this category yet.</p>
          )}

          {services.map((s) => (
            <div key={s.id} className="service-detail-card">
              <img
                src={s.image}
                alt={s.title}
                onClick={() => setLightboxImage(s.image)}
                style={{ cursor: 'zoom-in' }}
              />
              <div className="service-info">
                <h4>{s.title}</h4>
              </div>
            </div>
          ))}
        </div>

        {/* Suggested other categories */}
        {suggestions && suggestions.length > 0 && (
          <div className="other-categories container">
            <h3 className="suggestion-heading">Other categories you may like</h3>
            <div className="categories-grid">
              {suggestions.map((c) => (
                <Link key={c.id} to={`/category/${c.slug}`} className="category-card">
                  {/* Prefer explicit image fields; fall back to common alternatives then placeholder */}
                  <img src={c.image || c.imageUrl || c.thumbnail || 'https://placehold.co/600x400?text=UMA'} alt={c.name} />
                  <div className="category-name">{c.name}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
        {/* Lightbox modal */}
        {lightboxImage && (
          <div className="lightbox" onClick={() => { setLightboxImage(null); setLightboxScale(1); }}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button className="lightbox-close" onClick={() => { setLightboxImage(null); setLightboxScale(1); }}>&times;</button>
              <div className="lightbox-inner">
                <img
                  src={lightboxImage}
                  alt="Zoom"
                  style={{ transform: `scale(${lightboxScale})` }}
                />
              </div>

              <div className="lightbox-controls">
                <button
                  className="ctrl"
                  onClick={() => setLightboxScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))}
                  aria-label="Zoom in"
                >+</button>
                <button
                  className="ctrl"
                  onClick={() => setLightboxScale((s) => Math.max(1, +(s - 0.25).toFixed(2)))}
                  aria-label="Zoom out"
                >−</button>
                <button
                  className="ctrl"
                  onClick={async () => {
                    try {
                      if (navigator.share) {
                        await navigator.share({ title: category, text: `Check out this project image from ${category}`, url: lightboxImage });
                        setShareMessage('Shared successfully');
                      } else if (navigator.clipboard) {
                        await navigator.clipboard.writeText(lightboxImage);
                        setShareMessage('Image URL copied to clipboard');
                      } else {
                        // fallback
                        window.prompt('Copy this link', lightboxImage);
                      }
                    } catch (err) {
                      console.warn('Share failed', err);
                      setShareMessage('Unable to share');
                    }
                    setTimeout(() => setShareMessage(''), 2500);
                  }}
                  aria-label="Share image"
                >Share</button>
              </div>

              {shareMessage && <div className="share-msg">{shareMessage}</div>}
            </div>
          </div>
        )}
      </section>
      <Footer />
    </>
  );
}

export default ServiceDetail;